import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import type { BottomNavItem } from '../types';

interface BottomNavBarProps {
  items: BottomNavItem[];
}

/**
 * 하단 탭바 — AppTopBar/드로어와 별개로, 자주 오가는 화면을 엄지로 바로 전환하기 위한 탭.
 * 드로어(z-50)·백드롭(z-49) 위에 떠야 하므로 AppTopBar와 같은 규칙으로 z-index를 준다.
 */
export default function BottomNavBar({ items }: BottomNavBarProps) {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  if (!items.length) return null;

  return (
    <S.Bar>
      {items.map((item) => {
        const isActive = pathname === item.path || !!item.activePathPrefix?.some((prefix) => pathname.startsWith(prefix));

        return (
          <S.Tab
            key={item.id}
            type='button'
            $active={isActive}
            className='tw-tappable'
            aria-current={isActive ? 'page' : undefined}
            onClick={() => navigate(item.path)}>
            {item.Icon}
            <span>{item.label}</span>
          </S.Tab>
        );
      })}
    </S.Bar>
  );
}

const S = {
  Bar: styled.nav`
    grid-row: 3;
    grid-column: 1 / -1;
    position: relative;
    z-index: 51;
    display: flex;
    align-items: stretch;
    /* 안전영역 패딩을 height 안에 포함시켜야 한다(border-box) — AppTopBar와 같은 이유:
       height를 고정한 채 padding-bottom에 safe-area를 또 넣으면 홈 인디케이터가 있는 기기에서
       패딩이 박스 높이를 넘어 탭 라벨이 찌그러진다. --footer-h가 이미 그 합을 들고 있다(MainLayout). */
    height: var(--footer-h);
    padding: 0 0 env(safe-area-inset-bottom);
    border-top: 1px solid var(--color-table-line, #e5eaf8);
    background: var(--color-basic-white, #fff);
  `,
  Tab: styled.button<{ $active: boolean }>`
    padding-top: 0.5rem;
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2px;
    font-size: var(--text-dxs, 0.75rem);
    color: ${({ $active }) => ($active ? 'var(--color-support-blue, #0067dd)' : 'var(--color-gray-800, #767676)')};
  `,
};
